import React from 'react';

/**
 * The homepage's "Explore vehicles & equipment" categories. One icon style for every tile: the
 * same canvas, stroke weight and scale, in the tile's accent colour. Icons are simplified line
 * drawings, not photographs, so no tile can look photographed while another looks drawn.
 *
 * Seat counts are not part of a category. A coach can seat 54 passengers or fewer; the listing
 * records its own seat count, and no category implies one.
 */

const ICON_VIEWBOX = '0 0 96 56';

const Wheel = ({ cx, cy = 42, r = 6, a }) => (
  <circle cx={cx} cy={cy} r={r} fill="#fff" stroke={a} strokeWidth="2.5" />
);

const Outline = ({ d, a }) => (
  <path
    d={d}
    fill="none"
    stroke={a}
    strokeWidth="2.5"
    strokeLinejoin="round"
    strokeLinecap="round"
  />
);

const Svg = ({ a, children }) => (
  <svg viewBox={ICON_VIEWBOX} width="96" height="56" aria-hidden="true" focusable="false">
    {children}
  </svg>
);

const Car = ({ a, body, roof }) => (
  <Svg a={a}>
    <Outline d={body} a={a} />
    {roof ? <Outline d={roof} a={a} /> : null}
    <Wheel cx={24} a={a} />
    <Wheel cx={72} a={a} />
  </Svg>
);

const SuvIcon = ({ a }) => (
  <Car a={a} body="M10 40 L16 26 Q19 20 27 20 H60 Q68 20 74 28 L86 34 Q88 36 88 39 V40 H10 Z" />
);
const SedanIcon = ({ a }) => (
  <Car a={a} body="M8 40 L16 28 Q20 24 30 24 H54 Q64 24 70 31 L88 36 Q90 37 90 39 V40 H8 Z" />
);
const CoupeIcon = ({ a }) => (
  <Car a={a} body="M8 40 L18 30 Q24 24 40 24 Q56 24 64 31 L88 37 V40 H8 Z" />
);
const HatchbackIcon = ({ a }) => (
  <Car a={a} body="M10 40 V28 Q10 22 18 22 H60 Q66 22 70 29 L84 35 V40 Z" />
);
const ElectricIcon = ({ a }) => (
  <Car
    a={a}
    body="M8 40 L16 28 Q20 24 30 24 H54 Q64 24 70 31 L88 36 Q90 37 90 39 V40 H8 Z"
    roof="M48 6 L41 16 H48 L43 26"
  />
);
const HybridIcon = ({ a }) => (
  <Car
    a={a}
    body="M8 40 L16 28 Q20 24 30 24 H54 Q64 24 70 31 L88 36 Q90 37 90 39 V40 H8 Z"
    roof="M78 6 Q90 6 90 16 Q78 16 78 6 Z"
  />
);

const TruckIcon = ({ a }) => (
  <Svg a={a}>
    <Outline d="M6 40 V22 H50 V40 Z" a={a} />
    <Outline d="M50 30 H68 L82 38 V40 H50" a={a} />
    <Wheel cx={24} a={a} />
    <Wheel cx={70} a={a} />
  </Svg>
);
const FleetIcon = ({ a }) => (
  <Svg a={a}>
    <Outline d="M6 40 V10 H60 V40 Z" a={a} />
    <Outline d="M60 24 H76 L88 34 V40 H60" a={a} />
    <Wheel cx={22} a={a} />
    <Wheel cx={72} a={a} />
  </Svg>
);
const VanIcon = ({ a }) => (
  <Svg a={a}>
    <Outline d="M8 40 V12 H62 Q68 12 70 20 L86 32 V40 Z" a={a} />
    <Wheel cx={24} a={a} />
    <Wheel cx={72} a={a} />
  </Svg>
);

const CoachBusIcon = ({ a }) => (
  <Svg a={a}>
    <Outline d="M6 42 V14 Q6 10 12 10 H80 Q86 10 88 16 V42 Z" a={a} />
    <Outline d="M12 16 H82 V28 H12 Z" a={a} />
    <Wheel cx={24} cy={44} a={a} />
    <Wheel cx={72} cy={44} a={a} />
  </Svg>
);
const SchoolBusIcon = ({ a }) => (
  <Svg a={a}>
    <Outline d="M6 42 V14 Q6 10 12 10 H84 Q88 10 88 16 V42 Z" a={a} />
    <Outline d="M6 30 H88" a={a} />
    <Outline d="M12 16 H60 V26 H12 Z" a={a} />
    <Wheel cx={24} cy={44} a={a} />
    <Wheel cx={72} cy={44} a={a} />
  </Svg>
);
const HeavyEquipmentIcon = ({ a }) => (
  <Svg a={a}>
    <Outline d="M8 44 H52 V50 H8 Z" a={a} />
    <Outline d="M22 44 V34 H42 V44" a={a} />
    <Outline d="M42 34 L56 16 L66 26" a={a} />
    <Outline d="M28 34 L38 14 H50" a={a} />
  </Svg>
);
const BoatIcon = ({ a }) => (
  <Svg a={a}>
    <Outline d="M6 36 H90 L78 48 H18 Z" a={a} />
    <Outline d="M30 36 V24 H56 V36" a={a} />
    <Outline d="M44 24 V8" a={a} />
  </Svg>
);
const PowersportsIcon = ({ a }) => (
  <Svg a={a}>
    <Outline d="M22 32 H66 L74 22 H56" a={a} />
    <Outline d="M30 32 L36 42 H60" a={a} />
    <Wheel cx={22} cy={42} r={8} a={a} />
    <Wheel cx={72} cy={42} r={8} a={a} />
  </Svg>
);

export const VEHICLE_CATEGORIES = [
  {
    id: 'suv',
    label: 'SUVs',
    href: '/s?pub_bodyType=suv',
    bg: '#EEF2FF',
    accent: '#4338CA',
    Icon: SuvIcon,
  },
  {
    id: 'sedan',
    label: 'Sedans',
    href: '/s?pub_bodyType=sedan',
    bg: '#F0F9FF',
    accent: '#0369A1',
    Icon: SedanIcon,
  },
  {
    id: 'truck',
    label: 'Trucks',
    href: '/s?pub_bodyType=truck',
    bg: '#FFF7ED',
    accent: '#C2410C',
    Icon: TruckIcon,
  },
  {
    id: 'electric',
    label: 'EVs',
    href: '/s?pub_bodyType=electric',
    bg: '#F0FDFA',
    accent: '#0D9488',
    Icon: ElectricIcon,
  },
  {
    id: 'hybrid',
    label: 'Hybrids',
    href: '/s?pub_bodyType=hybrid',
    bg: '#F7FEE7',
    accent: '#65A30D',
    Icon: HybridIcon,
  },
  {
    id: 'fleet',
    label: 'Fleet',
    href: '/commercial',
    bg: '#F1F5F9',
    accent: '#334155',
    Icon: FleetIcon,
  },
  {
    id: 'van',
    label: 'Vans',
    href: '/s?pub_bodyType=van',
    bg: '#F8FAFC',
    accent: '#475569',
    Icon: VanIcon,
  },
  {
    id: 'coupe',
    label: 'Coupes',
    href: '/s?pub_bodyType=coupe',
    bg: '#FFF1F2',
    accent: '#BE123C',
    Icon: CoupeIcon,
  },
  {
    id: 'hatchback',
    label: 'Hatchbacks',
    href: '/s?pub_bodyType=hatchback',
    bg: '#FFF7ED',
    accent: '#EA580C',
    Icon: HatchbackIcon,
  },
  {
    id: 'coach-bus',
    label: 'Coach & Charter Buses',
    href: '/s?pub_vehicleCategory=coach-bus',
    bg: '#EFF6FF',
    accent: '#1D4ED8',
    Icon: CoachBusIcon,
  },
  {
    id: 'school-bus',
    label: 'School Buses',
    href: '/s?pub_vehicleCategory=school-bus',
    bg: '#FEFCE8',
    accent: '#CA8A04',
    Icon: SchoolBusIcon,
  },
  {
    id: 'heavy-equipment',
    label: 'Heavy Equipment',
    href: '/s?pub_vehicleCategory=heavy-equipment',
    bg: '#FFF7ED',
    accent: '#9A3412',
    Icon: HeavyEquipmentIcon,
  },
  {
    id: 'boat',
    label: 'Boats',
    href: '/s?pub_vehicleCategory=boat',
    bg: '#ECFEFF',
    accent: '#0E7490',
    Icon: BoatIcon,
  },
  {
    id: 'powersports',
    label: 'Powersports',
    href: '/s?pub_vehicleCategory=powersports',
    bg: '#F5F3FF',
    accent: '#6D28D9',
    Icon: PowersportsIcon,
  },
];
